import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    /** Shows a spinner and disables the button while a task is in flight. */
    busy?: boolean;
    /** Optional leading icon (e.g. a 14-16px Lucide glyph). */
    icon?: ReactNode;
    /** Optional trailing icon — for directional buttons ("Advance →"). Hidden while busy. */
    iconRight?: ReactNode;
    children?: ReactNode;
}

const VARIANT: Record<ButtonVariant, string> = {
    primary:
        'bg-[rgb(var(--color-accent))] text-white border border-transparent ' +
        'hover:bg-[rgb(var(--color-accent)/0.9)]',
    secondary:
        'bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-primary))] ' +
        'border border-[rgb(var(--color-divider))] ' +
        'hover:bg-[rgb(var(--color-surface-secondary))] hover:border-[rgb(var(--color-text-primary)/0.2)]',
    ghost:
        'bg-transparent text-[rgb(var(--color-text-primary))] border border-transparent ' +
        'hover:bg-[rgb(var(--color-surface-hover)/0.05)]',
    danger:
        'bg-[rgb(var(--color-rag-red))] text-white border border-transparent ' +
        'hover:bg-[rgb(var(--color-rag-red)/0.9)]',
};

const SIZE: Record<ButtonSize, string> = {
    sm: 'h-7 px-2.5 gap-1.5 text-annotation',
    md: 'h-9 px-3.5 gap-2 text-body-medium',
};

// Single button primitive for the kit. Replaces every hand-rolled
// `<button className="bg-[rgb(var(--color-accent))]...">` and the
// copy-pasted ExportButtons (see ExportButton.tsx, which builds on this).
//
// Side-by-side uniformity rule: `Button` and `Select` share one height per
// size (`md` = h-9, `sm` = h-7) and the same radius/border, so they line up
// perfectly when placed in a row. Keep every control in a horizontal group on
// ONE size — never mix `sm` and `md` side by side. Default to `md` for
// page/toolbar/dialog actions; reserve `sm` for dense inline rows (table-row
// actions, compact filter bars) where the whole row is `sm`.
const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        variant = 'secondary',
        size = 'md',
        busy = false,
        icon,
        iconRight,
        disabled,
        type = 'button',
        className,
        children,
        ...rest
    },
    ref,
) {
    return (
        <button
            ref={ref}
            type={type}
            disabled={disabled || busy}
            aria-busy={busy || undefined}
            className={cn(
                'inline-flex items-center justify-center rounded-button font-medium',
                'transition-colors duration-card-hover',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)]',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                VARIANT[variant],
                SIZE[size],
                className,
            )}
            {...rest}
        >
            {busy ? (
                <Loader2
                    aria-hidden="true"
                    className={cn('animate-spin', size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4')}
                />
            ) : (
                icon
            )}
            {children}
            {!busy && iconRight}
        </button>
    );
});

export default Button;
